import { partitionLayoutBlocks } from '~/utils/layoutChrome'
import { resolveLocalized } from '~/utils/resolveLocalized'
import { isBlockAvailable } from '~/composables/useBuilderMeta'
import { enabledModuleKeySet, useTenantSite } from '~/composables/useSiteModules'

export interface BlockDefinition {
  component: string
  props: Record<string, unknown>
  enabled?: boolean
}

export interface SitePage {
  title?: string | Record<string, string>
  slug?: string
  blocks?: BlockDefinition[]
  enabled?: boolean
  description?: string | Record<string, string>
  seoTitle?: string | Record<string, string>
  seoDescription?: string | Record<string, string>
  seoImage?: string
  noindex?: boolean
}

export interface SiteLayout {
  blocks?: BlockDefinition[]
}

export interface SiteContent {
  schemaVersion?: number
  i18n?: { enabled?: boolean; locales?: string[]; defaultLocale?: string }
  title?: unknown
  siteSettings?: {
    themePrimary?: string
    themeNeutral?: string
    themeColor?: string
    fontFamily?: string
    description?: string | Record<string, string>
  }
  layout?: SiteLayout
  pages?: Record<string, SitePage>
}

function pageKeyFromPath(content: SiteContent | null, path: string): string {
  if (path === '/' || path === '') return 'home'
  const normalized = path.replace(/\/$/, '') || '/'
  if (!content?.pages) return normalized.replace(/^\//, '') || 'home'
  for (const [key, page] of Object.entries(content.pages)) {
    const slug = page.slug || `/${key}`
    const slugNorm = slug.replace(/\/$/, '') || '/'
    if (slugNorm === normalized || slug === path) return key
    if (key === 'booking' && (normalized === '/appointment' || normalized === '/booking')) return key
  }
  return normalized.replace(/^\//, '') || 'home'
}

export function usePageBlocks() {
  const route = useRoute()
  const tenant = useTenantSite()
  const pending = computed(() => false)

  const content = computed<SiteContent | null>(() => {
    if (!tenant.value?.data) return null
    return tenant.value.data as unknown as SiteContent
  })

  const enabledModules = computed(() => enabledModuleKeySet(tenant.value?.modules ?? []))
  const siteId = computed(() => tenant.value?.id ?? null)

  const pageKey = computed(() => pageKeyFromPath(content.value, route.path))

  const availableLocales = computed(
    () => content.value?.i18n?.locales ?? ['fa', 'en'],
  )

  const siteDefaultLocale = computed(
    () => content.value?.i18n?.defaultLocale ?? availableLocales.value[0] ?? 'fa',
  )

  const locale = useState<string>('page-locale', () => 'fa')

  watch(
    siteDefaultLocale,
    (def) => {
      if (!def) return
      if (!availableLocales.value.includes(locale.value)) {
        locale.value = def
        return
      }
      if (import.meta.server) {
        locale.value = def
      }
    },
    { immediate: true },
  )

  function resolveValue(value: unknown, currentLocale: string): unknown {
    return resolveLocalized(
      value,
      currentLocale,
      availableLocales.value,
      content.value?.i18n?.defaultLocale,
    )
  }

  function allowBlock(block: BlockDefinition): boolean {
    if (block.enabled === false) return false
    return isBlockAvailable(block.component, enabledModules.value, siteId.value)
  }

  const page = computed<SitePage | null>(() => {
    const c = content.value
    if (!c?.pages) return null
    const found = c.pages[pageKey.value]
    if (!found) return null
    if (found.enabled === false) return { ...found, blocks: [] }
    return found
  })

  const localizedTitle = computed(() => {
    const p = page.value
    const t = p?.title ?? content.value?.title
    if (!t) return 'Site'
    return resolveValue(t, locale.value) as string
  })

  const layoutBlocks = computed(() => {
    const list = content.value?.layout?.blocks ?? []
    return list
      .filter(allowBlock)
      .map((block) => ({
        ...block,
        props: resolveValue(block.props, locale.value) as Record<string, unknown>,
      }))
  })

  const partitionedLayout = computed(() => partitionLayoutBlocks(layoutBlocks.value))

  const headerBlocks = computed(() => partitionedLayout.value.header)
  const midLayoutBlocks = computed(() => partitionedLayout.value.middle)
  const footerBlocks = computed(() => partitionedLayout.value.footer)

  const blocks = computed(() => {
    const list = page.value?.blocks ?? []
    return list
      .filter(allowBlock)
      .map((block) => ({
        ...block,
        props: resolveValue(block.props, locale.value) as Record<string, unknown>,
      }))
  })

  const siteSettings = computed(() => content.value?.siteSettings ?? {})

  return {
    content,
    pageKey,
    page,
    layoutBlocks,
    headerBlocks,
    midLayoutBlocks,
    footerBlocks,
    blocks,
    localizedTitle,
    pending,
    locale,
    availableLocales,
    siteSettings,
  }
}
